import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Star, CheckCircle2 } from "lucide-react";
import { SEO } from "../components/SEO";
import { PageHero } from "../components/PageHero";
import { Reveal, Eyebrow } from "../components/primitives";
import { StarRow } from "../components/ReviewsPreview";
import { API } from "../lib/site";

const initialForm = { name: "", email: "", organization: "", text: "" };

const RatingPicker = ({ value, onChange }) => (
    <div className="flex gap-2" role="radiogroup" aria-label="Your rating" data-testid="review-rating">
        {[1, 2, 3, 4, 5].map((s) => (
            <button
                key={s}
                type="button"
                role="radio"
                aria-checked={value === s}
                aria-label={`${s} star${s > 1 ? "s" : ""}`}
                data-testid={`rating-star-${s}`}
                onClick={() => onChange(s)}
                className="rounded-full p-1 transition-transform duration-200 hover:scale-110"
            >
                <Star
                    size={30}
                    aria-hidden="true"
                    className={s <= value ? "fill-[#C2A160] text-[#C2A160]" : "text-[#C2A160]/40"}
                />
            </button>
        ))}
    </div>
);

const Reviews = () => {
    const [form, setForm] = useState(initialForm);
    const [rating, setRating] = useState(0);
    const [errors, setErrors] = useState({});
    const [status, setStatus] = useState("idle");
    const [reviews, setReviews] = useState([]);

    useEffect(() => {
        axios
            .get(`${API}/reviews`)
            .then((res) => setReviews(res.data.reviews || []))
            .catch(() => setReviews([]));
    }, []);

    const set = (key) => (e) => {
        setForm((f) => ({ ...f, [key]: e.target.value }));
        setErrors((er) => ({ ...er, [key]: undefined }));
    };

    const submit = async (e) => {
        e.preventDefault();
        const er = {};
        if (form.name.trim().length < 2) er.name = "Please enter your name.";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) er.email = "Please enter a valid email address.";
        if (!rating) er.rating = "Please choose a star rating.";
        if (form.text.trim().length < 10) er.text = "Please write a few words about your experience.";
        setErrors(er);
        if (Object.keys(er).length) return;
        setStatus("submitting");
        try {
            await axios.post(`${API}/reviews`, { ...form, rating });
            setStatus("success");
            toast.success("Thank you — your review has been received.");
        } catch {
            setStatus("idle");
            toast.error("Something went wrong. Please try again.");
        }
    };

    const labelCls = "mb-2 block text-xs font-extrabold uppercase tracking-[0.2em] text-[#1A3B2B]/70";
    const errCls = "mt-2 text-xs font-semibold text-red-800";

    return (
        <main>
            <SEO
                title="Community Reviews | Global Green Hotel"
                description="Read genuine reviews of Global Green Hotel and share your own experience with the GGH community."
            />
            <PageHero
                testid="reviews-hero"
                eyebrow="Community Reviews"
                titleLines={["Share your experience", "with GGH"]}
                description="Honest words from hotels, partners and community members. Every published review is shared by a real person and approved by the GGH team before it appears."
            />

            <section data-testid="review-form-section" className="py-24 sm:py-32">
                <div className="mx-auto grid max-w-[1440px] gap-14 px-5 sm:px-8 lg:grid-cols-12 lg:gap-16 lg:px-12">
                    <div className="lg:col-span-5">
                        <div className="lg:sticky lg:top-32">
                            <Reveal>
                                <Eyebrow>Leave a Review</Eyebrow>
                                <h2 className="font-display mt-4 text-3xl font-medium tracking-tight text-[#0A1711] sm:text-4xl">
                                    Your voice helps GGH grow
                                </h2>
                            </Reveal>
                            <Reveal delay={0.15}>
                                <p className="mt-7 text-base leading-relaxed text-[#4A5D54] md:text-lg">
                                    Whether you attended a GGH conversation, explored a pilot idea,
                                    or simply believe in what we are building — your honest review
                                    helps others understand what GGH is like to work with.
                                </p>
                                <p className="mt-5 text-base leading-relaxed text-[#4A5D54] md:text-lg">
                                    Reviews are reviewed by our team before publication. Your email
                                    is never displayed.
                                </p>
                            </Reveal>
                        </div>
                    </div>

                    <div className="lg:col-span-7">
                        {status === "success" ? (
                            <Reveal>
                                <div
                                    data-testid="review-success"
                                    role="status"
                                    className="rounded-2xl border border-[#1A3B2B]/20 bg-white p-10 shadow-[0_20px_60px_rgba(10,23,17,0.07)]"
                                >
                                    <CheckCircle2 size={36} className="text-[#1A3B2B]" aria-hidden="true" />
                                    <p className="mt-5 text-base leading-relaxed text-[#0A1711] md:text-lg">
                                        Thank you for sharing your experience. Your review has been
                                        received and will appear on our website once it has been
                                        reviewed by the GGH team.
                                    </p>
                                </div>
                            </Reveal>
                        ) : (
                            <Reveal delay={0.1}>
                                <form onSubmit={submit} noValidate data-testid="review-form" className="grid gap-5">
                                    <div className="grid gap-5 sm:grid-cols-2">
                                        <div>
                                            <label htmlFor="rv-name" className={labelCls}>Name *</label>
                                            <input
                                                id="rv-name"
                                                data-testid="review-name"
                                                type="text"
                                                value={form.name}
                                                onChange={set("name")}
                                                aria-invalid={!!errors.name}
                                                className={`input-field ${errors.name ? "input-error" : ""}`}
                                                placeholder="Your name"
                                            />
                                            {errors.name && <p className={errCls} role="alert">{errors.name}</p>}
                                        </div>
                                        <div>
                                            <label htmlFor="rv-email" className={labelCls}>Email *</label>
                                            <input
                                                id="rv-email"
                                                data-testid="review-email"
                                                type="email"
                                                value={form.email}
                                                onChange={set("email")}
                                                aria-invalid={!!errors.email}
                                                className={`input-field ${errors.email ? "input-error" : ""}`}
                                                placeholder="you@example.com"
                                            />
                                            {errors.email && <p className={errCls} role="alert">{errors.email}</p>}
                                        </div>
                                    </div>
                                    <div>
                                        <label htmlFor="rv-org" className={labelCls}>
                                            Organization <span className="font-semibold normal-case tracking-normal text-[#4A5D54]/60">(optional)</span>
                                        </label>
                                        <input
                                            id="rv-org"
                                            data-testid="review-organization"
                                            type="text"
                                            value={form.organization}
                                            onChange={set("organization")}
                                            className="input-field"
                                            placeholder="Hotel, company, institution…"
                                        />
                                    </div>
                                    <div>
                                        <span className={labelCls}>Your Rating *</span>
                                        <RatingPicker value={rating} onChange={(v) => { setRating(v); setErrors((er) => ({ ...er, rating: undefined })); }} />
                                        {errors.rating && <p className={errCls} role="alert">{errors.rating}</p>}
                                    </div>
                                    <div>
                                        <label htmlFor="rv-text" className={labelCls}>Your Review *</label>
                                        <textarea
                                            id="rv-text"
                                            data-testid="review-text"
                                            rows={5}
                                            value={form.text}
                                            onChange={set("text")}
                                            aria-invalid={!!errors.text}
                                            className={`input-field resize-y ${errors.text ? "input-error" : ""}`}
                                            placeholder="Share your honest experience with Global Green Hotel…"
                                        />
                                        {errors.text && <p className={errCls} role="alert">{errors.text}</p>}
                                    </div>
                                    <div>
                                        <button
                                            type="submit"
                                            data-testid="review-submit"
                                            disabled={status === "submitting"}
                                            className="btn-green disabled:cursor-not-allowed disabled:opacity-60"
                                        >
                                            {status === "submitting" ? "Submitting…" : "Submit Review"}
                                        </button>
                                    </div>
                                </form>
                            </Reveal>
                        )}
                    </div>
                </div>
            </section>

            <section data-testid="reviews-list-section" className="grain relative overflow-hidden bg-[#07211A] py-24 sm:py-32">
                <div className="relative z-[2] mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
                    <Reveal>
                        <Eyebrow dark>Published Reviews</Eyebrow>
                        <h2 className="font-display mt-4 text-3xl font-medium tracking-tight text-[#FDFBF7] sm:text-4xl">
                            From the GGH community
                        </h2>
                    </Reveal>
                    {reviews.length === 0 ? (
                        <Reveal delay={0.15}>
                            <p data-testid="reviews-page-empty" className="mt-10 max-w-2xl text-base leading-relaxed text-[#FDFBF7]/70">
                                No reviews have been published yet. Reviews appear here after they
                                are shared by real members of our community and approved by the GGH
                                team.
                            </p>
                        </Reveal>
                    ) : (
                        <div className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="reviews-page-grid">
                            {reviews.map((r, i) => (
                                <Reveal key={r.id} delay={0.06 * i} y={22}>
                                    <figure className="flex h-full flex-col rounded-2xl border border-white/10 bg-[#04150D]/60 p-7">
                                        <StarRow rating={r.rating} />
                                        <blockquote className="mt-5 flex-1 text-sm leading-relaxed text-[#FDFBF7]/85">
                                            “{r.text}”
                                        </blockquote>
                                        <figcaption className="mt-6 border-t border-white/10 pt-4">
                                            <span className="block text-sm font-bold text-[#FDFBF7]">{r.name}</span>
                                            {r.organization && (
                                                <span className="mt-0.5 block text-xs text-[#FDFBF7]/50">{r.organization}</span>
                                            )}
                                        </figcaption>
                                    </figure>
                                </Reveal>
                            ))}
                        </div>
                    )}
                </div>
            </section>
        </main>
    );
};

export default Reviews;
